<template>
  <div class="sm:py-2 mb-20">
    <div class="sm:hidden">
      <div class="border-solid border-t border-gray-300 my-2" />
      <div class="p-2">
        <MenuButton icon="fa-home" :label="$t('menu.home')" url="/" :active="'home'" />
      </div>
      <div class="border-solid border-t border-gray-300 my-2" />
    </div>
    <div class="p-2">
      <MenuButton icon="fa-users" :label="$t('menu.customers')" url="/customer" :active="'customer'" />
      <MenuButton
        v-if="authStore.admin && authStore.licenses.some((x) => x.type === ProductType.BRANCHES)"
        key="branch"
        icon="fa-light fa-code-branch"
        :data-index="5"
        :label="$t('menu.branches')"
        url="/branches"
        :active="'branch'"
      />
    </div>
    <div class="border-solid border-t border-gray-300 my-2" />
    <div class="p-2">
      <router-link
        v-if="!authStore.isBranchManager && authStore.licenses.some((x) => x.type === ProductType.DEALER)"
        to="/enquiries"
      >
        <div class="flex items-center hover:bg-gray-300" :class="{ 'bg-gray-300': $route.meta.active === 'enquiries' }">
          <div class="p-1">
            <div class="rounded-full h-5 w-5 border-solid border-2 border-yellow-500 mr-4" />
          </div>
          <span>{{ $t('menu.viewEnquiries') }}</span>
        </div>
      </router-link>
      <template v-if="!authStore.isExtruderOrExpress">
        <router-link
          v-if="isReseller"
          :to="{ name: 'All Orders', query: { ...$route.query, type: 'order', status: '1' } }"
        >
          <div
            class="flex items-center hover:bg-gray-300"
            :class="{
              'bg-gray-300':
                $route.meta.active === 'all-orders' && $route.query.type === 'order' && $route.query.status === '1'
            }"
          >
            <div class="p-1">
              <div class="rounded-full h-5 w-5 border-solid border-2 border-orange-500 mr-4" />
            </div>
            <span>{{ $t('menu.pendingOrders') }}</span>
          </div>
        </router-link>
        <router-link
          v-if="isReseller"
          :to="{ name: 'All Orders', query: { ...$route.query, type: 'order', status: '2' } }"
        >
          <div
            class="flex items-center hover:bg-gray-300"
            :class="{
              'bg-gray-300':
                $route.meta.active === 'all-orders' && $route.query.type === 'order' && $route.query.status === '2'
            }"
          >
            <div class="p-1">
              <div class="rounded-full h-5 w-5 border-solid border-2 border-green-500 mr-4" />
            </div>
            <span>{{ $t('menu.placedOrders') }}</span>
          </div>
        </router-link>
        <router-link
          v-if="isReseller"
          :to="{ name: 'All Orders', query: { ...$route.query, type: 'order', status: '3' } }"
        >
          <div
            class="flex items-center hover:bg-gray-300"
            :class="{
              'bg-gray-300':
                $route.meta.active === 'all-orders' && $route.query.type === 'order' && $route.query.status === '3'
            }"
          >
            <div class="p-1">
              <div class="rounded-full h-5 w-5 border-solid border-2 border-red-500 mr-4" />
            </div>
            <span>{{ $t('menu.cancelledOrders') }}</span>
          </div>
        </router-link>
      </template>
    </div>
    <template v-if="authStore.admin">
      <div class="border-solid border-t border-gray-300 my-2" />
      <div class="p-2 group">
        <div
          class="flex items-center cursor-pointer z-50"
          :class="{ 'bg-gray-300': $route.meta.root === 'setup' }"
          @click.stop="isSetup = !isSetup"
        >
          <div class="p-1 mr-4">
            <i class="fal fa-fw fa-sharp fa-light fa-sitemap" />
          </div>
          <div class="flex flex-col justify-around">{{ $t('menu.setup.text') }}</div>
          <div class="ml-auto pr-3 text-xs text-gray-600">
            <i v-if="isSetup" class="fal fa-chevron-up" />
            <i v-else class="fal fa-chevron-down" />
          </div>
        </div>
        <div class="relative overflow-hidden">
          <div v-if="isSetup" class="p-2">
            <ul class="flex flex-col gap-1">
              <li>
                <MenuButton icon="fa-user" :label="$t('menu.setup.account')" :url="'/setup/account'" active="account" />
              </li>
              <li>
                <MenuButton
                  icon="fa-sharp fa-light fa-user-helmet-safety"
                  :label="$t('menu.setup.staff')"
                  :url="'/setup/staff'"
                  active="staff"
                />
              </li>
              <li>
                <MenuButton
                  icon="fa-box"
                  :label="$t('menu.setup.productSettings')"
                  :url="'/setup/product-settings'"
                  active="product-settings"
                />
              </li>
              <li>
                <MenuButton
                  v-if="!authStore.isExtruderOrExpress"
                  icon="fa-table"
                  :label="$t('menu.setup.costsPrices')"
                  :url="'/setup/costs-prices'"
                  active="costs-prices"
                />
              </li>
              <li>
                <MenuButton
                  icon="fa-paint-brush"
                  :label="$t('menu.setup.styling')"
                  :url="'/setup/styling'"
                  active="styling"
                />
              </li>
              <li>
                <MenuButton
                  v-if="authStore.licenses.some((x) => x.type === ProductType.DEALER)"
                  icon="fa-desktop"
                  label="Lead Capture"
                  :url="'/setup/lead-capture'"
                  active="lead-capture"
                />
              </li>
              <li>
                <MenuButton
                  v-if="!authStore.isExtruderOrExpress"
                  icon="fa-truck"
                  :label="$t('menu.setup.delivery')"
                  :url="'/setup/delivery'"
                  active="delivery"
                />
              </li>
              <li v-if="authStore.isPayment">
                <MenuButton
                  icon="fa-credit-card"
                  :label="$t('menu.setup.paymentGateway')"
                  :url="'/setup/payment-gateway'"
                  active="payment-gateway"
                />
              </li>
              <li v-if="!authStore.isExtruderOrExpress">
                <MenuButton
                  icon="fa-layer-group"
                  :label="$t('menu.setup.retailOptionControl')"
                  :url="'/setup/retail-option-control'"
                  active="retail-option-control"
                />
              </li>
            </ul>
          </div>
        </div>
      </div>
    </template>
    <div class="border-solid border-t border-gray-300 my-2" />
    <div class="p-2">
      <MenuButton
        icon="fa-book-open"
        :label="$t('menu.resourceCentre')"
        :url="isLinkedToBranch ? '/marketing' : '/manage-marketing'"
        :active="'marketing'"
      />
      <MenuButton
        icon="fa-megaphone"
        :label="$t('menu.news')"
        :url="isLinkedToBranch ? '/news' : '/manage-news'"
        :active="'manage-news'"
      />
      <MenuButton
        v-if="isReseller && !authStore.isExtruderOrExpress && !authStore.isBranchManager"
        icon="fa-chart-line"
        label="KPI"
        url="/kpi"
        :active="'kpi'"
      />
    </div>
    <template v-if="!authStore.isExtruderOrExpress">
      <div class="border-solid border-t border-gray-300 my-2" />
      <div class="p-2">
        <MenuButton
          icon="fa-store"
          :label="$t('menu.store', { touch: 'QUOTILA' })"
          url="/touch-store-admin"
          :active="'touch-store'"
        />
        <MenuButton
          v-if="false"
          icon="fa-square-question"
          :label="$t('menu.knowledgeBase')"
          :external_link="true"
          url="https://bmgroupholdingslimited.freshdesk.com/support/solutions/101000252273"
          :active="''"
        />
      </div>
    </template>
    <SupportPopUp :company-name="authStore.companyName" />
    <div class="border-solid border-t border-gray-300 my-2" />
    <div class="p-2">
      <LocalesDropdown class="w-full pr-3" bg-class="bg-white" bg-hover-class="hover:bg-gray-300" />
    </div>
  </div>
</template>

<script>
import MenuButton from '@/components/shared/nav/MenuButton.vue'
import SupportPopUp from '@/components/shared/nav/SupportPopUp.vue'
import { useAuthorisation } from '@/composables/authorisation'
import { ProductType } from '@/enums'
import LocalesDropdown from '@/features/locales/components/LocalesDropdown.vue'
import { useAuthStore } from '@/pinia/auth'
import { useCustomerStore } from '@/pinia/customer'
import { useStyleStore } from '@/pinia/style'
import { mapStores } from 'pinia'

export default {
  name: 'BusinessMenuItems',

  components: {
    MenuButton,
    SupportPopUp,
    LocalesDropdown
  },
  props: {
    initialSetup: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      user: undefined,
      isSetup: false
    }
  },
  setup() {
    const { isReseller } = useAuthorisation()
    return {
      isReseller,
      ProductType
    }
  },
  computed: {
    ...mapStores(useAuthStore, useStyleStore, useCustomerStore),
    isBnQ() {
      return this.styleStore.fabricator.name === 'B & Q'
    },
    isLinkedToBranch() {
      return this.customerStore.customer?.users.some(
        ({ email, branchCompanyId }) => branchCompanyId && this.authStore.email === email
      )
    }
  },
  created() {
    this.isSetup = this.initialSetup
  }
}
</script>
